/**
 * 玩家留资（昵称 + 邮箱）。投递到自建后端 `/api/lead`（server/app.py），
 * 服务端按 player_id 落库、重复提交取最新（plan v2 §3）。
 *
 * 无论投递成功与否都会先写本地：玩家不该因为网络问题被反复要一遍信息。
 */

import { playerId } from './telemetry.js';

const KEY = 'sd_lead';

/** 留资投递地址。空 = 只存本地。 */
const ENDPOINT = '/api/lead';

/**
 * @typedef {object} Lead
 * @property {string} name
 * @property {string} email
 * @property {string} at   ISO 时间戳
 * @property {boolean} [sent] 是否已成功投递到 ENDPOINT
 */

/**
 * 读已保存的留资；没填过返回 null。
 * @returns {Lead | null}
 */
export function loadLead() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const lead = JSON.parse(raw);
        if (!lead || (!lead.name && !lead.email)) return null;
        return lead;
    } catch {
        return null;
    }
}

/** @param {Lead} lead */
function persist(lead) {
    try {
        localStorage.setItem(KEY, JSON.stringify(lead));
    } catch { /* 隐私模式等，忽略 */ }
}

/**
 * 邮箱格式校验。故意放宽 —— 只挡明显写错的，
 * 真正的有效性只有发信才知道，卡太严会把合法地址拒之门外。
 * @param {string} v
 */
export function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
}

/**
 * 提交留资。
 * @param {{ name: string, email: string }} input
 * @returns {Promise<{ ok: boolean, error?: 'name' | 'email', lead?: Lead }>}
 */
export async function submitLead({ name, email }) {
    name = String(name || '').trim().slice(0, 40);
    email = String(email || '').trim().slice(0, 120);

    if (!name) return { ok: false, error: 'name' };
    if (!isValidEmail(email)) return { ok: false, error: 'email' };

    /** @type {Lead} */
    const lead = { name, email, at: new Date().toISOString(), sent: false };

    // 先落本地：投递失败也不该再骚扰玩家
    persist(lead);

    if (ENDPOINT) {
        try {
            const res = await fetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...lead, source: 'web-daily', player_id: playerId() }),
            });
            if (res.ok) {
                lead.sent = true;
                persist(lead);
            }
        } catch {
            // 网络失败：本地已存，将来可做补投
        }
    }

    return { ok: true, lead };
}

/** 清除留资（调试用）。 */
export function clearLead() {
    try {
        localStorage.removeItem(KEY);
    } catch { /* 忽略 */ }
}
